'use strict';

const SERIES_TEMPLATE_SCHEMA = 'tiny-fox/series-template@1';
const PAGE_COMPOSITION_SCHEMA = 'tiny-fox/page-composition@1';
const PAGE_COMPOSITION_SCHEMA_V2 = 'tiny-fox/page-composition@2';
const PRODUCTION_PAGE_FLOW_SCHEMA = 'tiny-fox/production-page-flow@1';
const AUTHORITATIVE_MEASUREMENT_SCHEMA = 'tiny-fox/font-measurement@1';
const PRODUCTION_LAYOUT_PROFILE_SCHEMA = 'tiny-fox/production-layout-profile@1';
const STORYBOOK_LAYOUT_VERSION = '22.1.0';
const PRODUCTION_LAYOUT_VERSION = '22.2.0';
const BUNDLED_FONT_ASSET_VERSION = 'fontsource-5.3.0-noto-color-emoji-5.3.1';
const PIXEL_ANALYZER_VERSION = 'storybook-pixel-analyzer-v1';
const TINY_LITTLE_FOX_SEQUEL_TEMPLATE_ID = 'tiny-little-fox-sequel-v1';
const NEUTRAL_PRODUCTION_TEMPLATE_ID = 'neutral-production-v1';

const PAGE_COMPOSITION_STRATEGIES = Object.freeze([
  'image-only',
  'quiet-zone',
  'protected-panel',
  'split-vignette',
  'illustrated-text-page'
]);
const PAGE_COMPOSITION_TREATMENTS = Object.freeze([
  'integrated',
  'paper-fade',
  'solid-paper',
  'framed-vignette'
]);
const QUICK_CREATE_COMPOSITION_MODES = Object.freeze([
  'auto-book-rhythm',
  'separate-reading-panels',
  'integrated-quiet-zones',
  'illustrated-text-pages'
]);
const TEXT_DENSITIES = Object.freeze(['airy', 'balanced', 'compact']);
const QUICK_CREATE_TREATMENTS = Object.freeze(['solid-paper', 'paper-fade']);

const deepFreeze = value => {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
};

const QUICK_CREATE_CONTROL_OPTIONS = deepFreeze({
  composition: [
    { value: 'auto-book-rhythm', label: 'Auto Book Rhythm' },
    { value: 'separate-reading-panels', label: 'Separate Reading Panels' },
    { value: 'integrated-quiet-zones', label: 'Integrated Quiet Zones' },
    { value: 'illustrated-text-pages', label: 'Illustrated Text Pages' }
  ],
  density: [
    { value: 'airy', label: 'Airy' },
    { value: 'balanced', label: 'Balanced' },
    { value: 'compact', label: 'Compact' }
  ],
  treatment: [
    { value: 'solid-paper', label: 'Solid Paper' },
    { value: 'paper-fade', label: 'Paper Fade' }
  ]
});

/**
 * Measured from the author-provided 8.25 x 11 hardcover manuscript. This is a
 * series system, not a request to copy page content: prose and illustrations
 * remain original while the repeatable rhythm, typography and safe regions are
 * deterministic.
 */
const TINY_LITTLE_FOX_SEQUEL_TEMPLATE = deepFreeze({
  schema: SERIES_TEMPLATE_SCHEMA,
  id: TINY_LITTLE_FOX_SEQUEL_TEMPLATE_ID,
  version: STORYBOOK_LAYOUT_VERSION,
  name: 'Tiny Little Fox sequel',
  description: 'Pale paper, separate read-aloud beats, and contained spot illustrations with no artwork behind text.',
  reference: {
    pageCount: 78,
    pdfPagePoints: { width: 612, height: 810 },
    trimCanvas: '8.25x11-hc',
    referenceSha256: '557ebd80cfcef1fab6415b8b7fb2b89539369c1868cfe9a777bc690e1d4aa5b4'
  },
  audience: { readingAge: '6-8' },
  palette: {
    mintPaper: '#F1F8F0',
    creamPaper: '#FFFCF3',
    ink: '#141414',
    warmInk: '#2A211B',
    accentBlue: '#A9D6E4',
    foxOrange: '#EE8425'
  },
  typography: {
    narration: { fontFamily: 'Bebas Neue', fontWeight: '400', lineHeight: 1.12 },
    dialogue: { fontFamily: 'Patrick Hand', fontWeight: '400', lineHeight: 1.15 },
    instruction: { fontFamily: 'Merriweather', fontWeight: '400', lineHeight: 1.3 },
    title: { fontFamily: 'Permanent Marker', fontWeight: '400', lineHeight: 1.08 }
  },
  copyBudget: {
    preferredWordsPerPage: { minimum: 18, maximum: 48 },
    maximumTextBeats: 3,
    preferredNarrationSentenceWords: { minimum: 7, maximum: 16 },
    preferredDialogueWords: { minimum: 2, maximum: 10 }
  },
  storyStylePrompt: [
    'Write an original present-tense read-aloud for ages 6–8 in the established Tiny Little Fox series voice.',
    'Give each page one concrete action and 1–3 separately renderable text beats, normally 18–48 words total.',
    'Use calm, clear narration; brief expressive inner dialogue; gentle humor; and an occasional direct question that lets the child reader help.',
    'Prefer sentences of 7–16 words and dialogue of 2–10 words. Repeat important nouns when that improves clarity.',
    'Keep medical or safety facts supportive and precise, flag dose-specific guidance for expert review, and never reuse wording from a reference page.'
  ].join(' '),
  visualStylePrompt: [
    'Create an original clean ink-and-color children’s spot illustration with expressive animal acting, confident dark outlines, and restrained hand-painted texture.',
    'Create a contained vignette or isolated character group that fills roughly 80–90 percent of the returned art canvas.',
    'Use a transparent background when supported; otherwise use one uniform pale mint or warm cream paper field with no rectangular border or edge vignette.',
    'Do not reserve story-text space inside the illustration—the application reserves a separate protected reading region on the page.',
    'Keep the silhouette readable, props purposeful, and palette led by fox orange, soft green, warm brown, and small blue accents.',
    'Illustration only: no letters, captions, speech bubbles, page numbers, watermarks, frames, or important details at crop edges.'
  ].join(' '),
  pageFamilies: ['spot-narration', 'spot-dialogue', 'protected-vignette', 'illustrated-text-page']
});

const NEUTRAL_PRODUCTION_TEMPLATE = deepFreeze({
  schema: PRODUCTION_LAYOUT_PROFILE_SCHEMA,
  id: NEUTRAL_PRODUCTION_TEMPLATE_ID,
  version: PRODUCTION_LAYOUT_VERSION,
  name: 'Neutral production storybook',
  description: 'Accessible locally bundled typography and neutral paper colors for books without a named series preset.',
  palette: {
    mintPaper: '#F3F7F2',
    creamPaper: '#FFFCF5',
    ink: '#171717',
    warmInk: '#2A2521',
    accentBlue: '#A8CCD8',
    foxOrange: '#D9782D'
  },
  typography: {
    narration: { fontFamily: 'Merriweather', fontWeight: '400', lineHeight: 1.3 },
    dialogue: { fontFamily: 'Patrick Hand', fontWeight: '400', lineHeight: 1.2 },
    instruction: { fontFamily: 'Noto Sans', fontWeight: '400', lineHeight: 1.35 },
    title: { fontFamily: 'Playfair Display', fontWeight: '700', lineHeight: 1.12 }
  },
  copyBudget: {
    preferredWordsPerPage: { minimum: 12, maximum: 55 },
    maximumTextBeats: 4
  },
  layout: {
    minimumSafeMarginRatio: 0.06,
    protectedTextWidthRatio: 0.82,
    defaultComposition: 'auto-book-rhythm'
  }
});

const SERIES_TEMPLATES = deepFreeze({
  [TINY_LITTLE_FOX_SEQUEL_TEMPLATE_ID]: TINY_LITTLE_FOX_SEQUEL_TEMPLATE
});

const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
const round = value => Math.round(finite(value));
const wordsIn = value => String(value || '').trim().split(/\s+/u).filter(Boolean).length;
const safeId = value => String(value || 'page').replace(/[^a-zA-Z0-9_-]/g, '-').slice(0, 80) || 'page';

const getSeriesTemplate = templateId => {
  const template = SERIES_TEMPLATES[String(templateId || '')];
  if (!template) throw new TypeError(`Unsupported series template: ${String(templateId)}`);
  return template;
};

const isSeriesTemplateId = templateId => Boolean(SERIES_TEMPLATES[String(templateId || '')]);

const createSeriesStyleDirection = templateId => {
  const template = getSeriesTemplate(templateId);
  return {
    seriesTemplateId: template.id,
    referenceBook: template.name,
    canvasSize: template.reference.trimCanvas,
    readingAge: template.audience.readingAge,
    storyStylePrompt: template.storyStylePrompt,
    visualStylePrompt: template.visualStylePrompt
  };
};

const normalizeTextBoxes = textBoxes => (Array.isArray(textBoxes) ? textBoxes : [])
  .map((box, index) => ({
    type: String(box && box.type || 'narration').toLowerCase(),
    character: box && box.character ? String(box.character) : undefined,
    content: String(box && box.content || '').trim(),
    sourceTextBoxId: box && box.sourceTextBoxId ? String(box.sourceTextBoxId) : undefined,
    sourceIndex: index
  }))
  .filter(box => box.content);

const resolvePageFamily = ({ textBoxes, hasIllustration = true }) => {
  const boxes = normalizeTextBoxes(textBoxes);
  const totalWords = boxes.reduce((total, box) => total + wordsIn(box.content), 0);
  const dialogueCount = boxes.filter(box => box.type === 'dialogue').length;
  if (!hasIllustration || totalWords > 88 || boxes.length > 4) return 'illustrated-text-page';
  if (dialogueCount >= Math.ceil(Math.max(1, boxes.length) / 2)
    && boxes.length <= 3 && totalWords <= 38) return 'spot-dialogue';
  if (boxes.length <= 3 && totalWords <= 52) return 'spot-narration';
  return 'protected-vignette';
};

const estimateLineCount = (content, width, fontSize, family) => {
  // Bebas Neue is deliberately condensed. The measured series manuscript fits
  // roughly 55 characters into a 372 pt narration line at 20.7 pt. Keeping the
  // family-specific factor here prevents phantom lines from pushing later beats
  // away from the reference page rhythm.
  const widthFactor = family === 'Bebas Neue' ? 0.35 : family === 'Patrick Hand' ? 0.5 : 0.56;
  const charactersPerLine = Math.max(8, Math.floor(width / Math.max(1, fontSize * widthFactor)));
  const tokens = String(content || '').split(/(\s+)/u).filter(Boolean);
  let lines = 1;
  let used = 0;
  for (const token of tokens) {
    const size = token.length;
    if (used > 0 && used + size > charactersPerLine) {
      lines += Math.max(1, Math.ceil(size / charactersPerLine));
      used = size % charactersPerLine;
    } else {
      used += size;
    }
  }
  return Math.max(1, lines);
};

const typographyFor = (template, box, family) => {
  if (box.type === 'dialogue' || family === 'spot-dialogue') return template.typography.dialogue;
  if (/instruction|fact|activity/u.test(box.type)) return template.typography.instruction;
  if (/title/u.test(box.type)) return template.typography.title;
  return template.typography.narration;
};

const textRoleFor = box => box.type === 'dialogue' ? 'dialogue'
  : /title/u.test(box.type) ? 'title'
    : 'narration';

const buildSequentialTextElements = ({
  boxes, template, pageId, family, x, y, width, maximumHeight, fontSize, gap,
  slotStride
}) => {
  const prepared = boxes.map(box => {
    const typography = typographyFor(template, box, family);
    const lines = estimateLineCount(box.content, width, fontSize, typography.fontFamily);
    const height = Math.ceil(lines * fontSize * typography.lineHeight);
    return { box, typography, height };
  });
  const required = slotStride
    ? prepared.reduce((maximum, item, index) => Math.max(maximum, index * slotStride + item.height), 0)
    : prepared.reduce((sum, item) => sum + item.height, 0)
      + Math.max(0, prepared.length - 1) * gap;
  const scale = required > maximumHeight ? maximumHeight / required : 1;
  const adjustedFontSize = Math.max(18, Math.floor(fontSize * scale));
  let nextY = y;
  const elements = prepared.map((item, index) => {
    const lines = estimateLineCount(item.box.content, width, adjustedFontSize, item.typography.fontFamily);
    const height = Math.ceil(lines * adjustedFontSize * item.typography.lineHeight);
    const elementY = slotStride ? y + index * slotStride : nextY;
    const element = {
      id: `${safeId(pageId)}-text-${index + 1}`,
      content: item.box.content,
      x: round(x), y: round(elementY), width: round(width), height: round(height),
      fontSize: adjustedFontSize,
      fontFamily: item.typography.fontFamily,
      fontWeight: item.typography.fontWeight,
      fontStyle: 'normal',
      lineHeight: item.typography.lineHeight,
      padding: 0,
      color: template.palette.ink,
      rotation: 0,
      backgroundColor: 'transparent',
      backgroundOpacity: 0,
      borderWidth: 0,
      textAlign: 'left',
      verticalAlign: 'top',
      layoutRole: textRoleFor(item.box),
      sourceTextBoxId: item.box.sourceTextBoxId || `${safeId(pageId)}-source-${item.box.sourceIndex + 1}`
    };
    if (!slotStride) nextY += height + gap;
    return element;
  });
  const requiredHeight = slotStride
    ? elements.reduce((maximum, element) => Math.max(maximum, element.y + element.height - y), 0)
    : nextY - y - (elements.length ? gap : 0);
  return { elements, fontSize: adjustedFontSize, requiredHeight };
};

const buildDialogueTextElements = ({ boxes, template, pageId, width, height }) => {
  const fontSize = clamp(round(Math.min(width, height) * 0.034), 20, 29);
  const placements = [
    { x: 0.08, y: 0.18, width: 0.48, rotation: -2 },
    { x: 0.42, y: 0.27, width: 0.45, rotation: 2 },
    { x: 0.17, y: 0.35, width: 0.55, rotation: -1 }
  ];
  return boxes.map((box, index) => {
    const placement = placements[index] || placements[placements.length - 1];
    const boxWidth = width * placement.width;
    const lines = estimateLineCount(box.content, boxWidth, fontSize, template.typography.dialogue.fontFamily);
    return {
      id: `${safeId(pageId)}-text-${index + 1}`,
      content: box.content,
      x: round(width * placement.x),
      y: round(height * placement.y),
      width: round(boxWidth),
      height: round(lines * fontSize * template.typography.dialogue.lineHeight),
      fontSize,
      fontFamily: template.typography.dialogue.fontFamily,
      fontWeight: template.typography.dialogue.fontWeight,
      fontStyle: 'normal',
      lineHeight: template.typography.dialogue.lineHeight,
      padding: 0,
      color: template.palette.ink,
      rotation: placement.rotation,
      backgroundColor: 'transparent',
      backgroundOpacity: 0,
      borderWidth: 0,
      textAlign: 'left',
      verticalAlign: 'top',
      layoutRole: 'dialogue',
      sourceTextBoxId: box.sourceTextBoxId || `${safeId(pageId)}-source-${box.sourceIndex + 1}`
    };
  });
};

const normalizedRegion = (bounds, width, height, label = 'Protected story text') => ({
  label,
  role: 'text-safe',
  x: Number((bounds.x / width).toFixed(6)),
  y: Number((bounds.y / height).toFixed(6)),
  width: Number((bounds.width / width).toFixed(6)),
  height: Number((bounds.height / height).toFixed(6)),
  confidence: 1
});

const providerAspectRatioFor = bounds => {
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) return undefined;
  const ratio = bounds.width / bounds.height;
  const candidates = [
    ['1:1', 1], ['3:4', 3 / 4], ['4:3', 4 / 3], ['9:16', 9 / 16], ['16:9', 16 / 9]
  ];
  return candidates.reduce((best, candidate) => (
    Math.abs(Math.log(ratio / candidate[1])) < Math.abs(Math.log(ratio / best[1])) ? candidate : best
  ))[0];
};

const planSeriesPage = input => {
  const template = getSeriesTemplate(input && input.templateId);
  const width = round(input && input.canvasSize && input.canvasSize.width);
  const height = round(input && input.canvasSize && input.canvasSize.height);
  if (width < 100 || height < 100) throw new TypeError('canvasSize must contain dimensions of at least 100 pixels');
  const boxes = normalizeTextBoxes(input && input.textBoxes);
  const totalWords = boxes.reduce((total, box) => total + wordsIn(box.content), 0);
  const requestedIllustration = input && input.hasIllustration !== false;
  const family = input && input.pageFamily
    ? String(input.pageFamily)
    : resolvePageFamily({ textBoxes: boxes, hasIllustration: requestedIllustration });
  if (!template.pageFamilies.includes(family)) throw new TypeError(`Unsupported page family: ${family}`);

  const pageId = safeId(input && input.pageId);
  const warnings = [];
  let textBounds;
  let imageBounds = { x: 0, y: 0, width: 0, height: 0 };
  let textElements = [];
  let imageRequired = requestedIllustration && family !== 'illustrated-text-page';

  if (family === 'spot-dialogue') {
    textBounds = { x: round(width * 0.06), y: round(height * 0.12), width: round(width * 0.84), height: round(height * 0.31) };
    imageBounds = { x: round(width * 0.08), y: round(height * 0.41), width: round(width * 0.84), height: round(height * 0.5) };
    textElements = buildDialogueTextElements({ boxes, template, pageId, width, height });
  } else if (family === 'spot-narration') {
    textBounds = { x: round(width * 0.068), y: round(height * 0.09), width: round(width * 0.625), height: round(height * 0.26) };
    imageBounds = { x: round(width * 0.15), y: round(height * 0.35), width: round(width * 0.78), height: round(height * 0.53) };
    const layout = buildSequentialTextElements({
      boxes, template, pageId, family,
      x: textBounds.x, y: textBounds.y, width: textBounds.width,
      maximumHeight: textBounds.height,
      fontSize: clamp(round(Math.min(width, height) * 0.034), 19, 29),
      gap: clamp(round(height * 0.017), 12, 22),
      slotStride: round(height * 0.1)
    });
    textElements = layout.elements;
    if (layout.requiredHeight > textBounds.height + 1) warnings.push('COPY_EXCEEDS_SPOT_TEXT_REGION');
  } else if (family === 'protected-vignette') {
    textBounds = { x: round(width * 0.07), y: round(height * 0.055), width: round(width * 0.86), height: round(height * 0.38) };
    imageBounds = { x: round(width * 0.1), y: round(height * 0.47), width: round(width * 0.8), height: round(height * 0.42) };
    const layout = buildSequentialTextElements({
      boxes, template, pageId, family,
      x: textBounds.x, y: textBounds.y, width: textBounds.width,
      maximumHeight: textBounds.height,
      fontSize: clamp(round(Math.min(width, height) * 0.032), 19, 28),
      gap: clamp(round(height * 0.014), 10, 18)
    });
    textElements = layout.elements;
    if (layout.requiredHeight > textBounds.height + 1) warnings.push('COPY_EXCEEDS_PROTECTED_TEXT_REGION');
  } else {
    imageRequired = false;
    textBounds = { x: round(width * 0.09), y: round(height * 0.1), width: round(width * 0.82), height: round(height * 0.78) };
    const layout = buildSequentialTextElements({
      boxes, template, pageId, family,
      x: textBounds.x, y: textBounds.y, width: textBounds.width,
      maximumHeight: textBounds.height,
      fontSize: clamp(round(Math.min(width, height) * 0.03), 19, 27),
      gap: clamp(round(height * 0.022), 14, 26)
    });
    textElements = layout.elements;
    if (layout.requiredHeight > textBounds.height + 1) warnings.push('COPY_REQUIRES_CONTINUATION');
  }

  if (textElements.some(element => element.fontSize < 18)) warnings.push('FONT_BELOW_PRINT_FLOOR');
  if (totalWords > template.copyBudget.preferredWordsPerPage.maximum) warnings.push('COPY_ABOVE_SERIES_PREFERENCE');
  const safeRegion = normalizedRegion(textBounds, width, height);
  const imageUrl = input && input.imageUrl ? String(input.imageUrl) : '';
  const imageElements = imageRequired && imageUrl ? [{
    id: `${pageId}-illustration-1`,
    url: imageUrl,
    x: imageBounds.x,
    y: imageBounds.y,
    width: imageBounds.width,
    height: imageBounds.height,
    rotation: 0,
    fit: 'contain',
    feather: 8,
    bleed: 'none',
    layoutRole: 'illustration',
    altText: String(input && input.altText || 'Story illustration')
  }] : [];

  return {
    schema: PAGE_COMPOSITION_SCHEMA,
    version: STORYBOOK_LAYOUT_VERSION,
    templateId: template.id,
    pageFamily: family,
    strategy: family === 'illustrated-text-page' ? 'illustrated-text-page' : 'split-vignette',
    treatment: 'solid-paper',
    backgroundColor: family === 'spot-dialogue' ? template.palette.mintPaper : template.palette.creamPaper,
    canvasSize: { width, height },
    textBounds,
    imageBounds,
    textElements,
    imageElements,
    imageRequired,
    providerAspectRatio: providerAspectRatioFor(imageBounds),
    totalWords,
    textSafeRegions: [safeRegion],
    requiresContinuation: warnings.includes('COPY_REQUIRES_CONTINUATION'),
    warnings
  };
};

const createSeriesIllustrationPrompt = ({ templateId, scenePrompt, pageFamily }) => {
  const template = getSeriesTemplate(templateId);
  const family = pageFamily || 'spot-narration';
  return [
    String(scenePrompt || 'A warm Tiny Little Fox story moment.'),
    template.visualStylePrompt,
    `Composition family: ${family}. The application will place the illustration inside a protected art region; keep every face, paw, prop, and shadow fully inside the vignette.`,
    'Return illustration only and keep all words out of the image.'
  ].join(' ');
};

// Production composition ---------------------------------------------------
//
// The legacy sequel planner above remains available for stored @1 pages. The
// production API below is deliberately measurement-adapter driven: it never
// guesses line geometry and therefore cannot accidentally authorize an image
// call when the pinned fonts are unavailable.

const PLACEMENT_IDS = Object.freeze([
  'top', 'bottom', 'left', 'right',
  'top-left', 'top-right', 'bottom-left', 'bottom-right'
]);

const SHA256_CONSTANTS = Object.freeze([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
]);

const rotateRight = (value, amount) => (value >>> amount) | (value << (32 - amount));

const asBytes = value => {
  if (value instanceof Uint8Array) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  throw new TypeError('Expected an RGBA ArrayBuffer or typed-array buffer.');
};

const utf8Bytes = value => {
  const content = String(value == null ? '' : value);
  if (typeof TextEncoder === 'function') return new TextEncoder().encode(content);
  const encoded = unescape(encodeURIComponent(content)); // eslint-disable-line no-undef
  return Uint8Array.from(encoded, character => character.charCodeAt(0));
};

/** A dependency-free SHA-256 implementation shared by browser and server. */
const sha256HexBytes = value => {
  const input = asBytes(value);
  const paddedLength = Math.ceil((input.length + 9) / 64) * 64;
  const bytes = new Uint8Array(paddedLength);
  bytes.set(input);
  bytes[input.length] = 0x80;
  const bitLength = input.length * 8;
  const view = new DataView(bytes.buffer);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000), false);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);

  const hash = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
  ];
  const schedule = new Uint32Array(64);
  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      schedule[index] = view.getUint32(offset + index * 4, false);
    }
    for (let index = 16; index < 64; index += 1) {
      const left = schedule[index - 15];
      const right = schedule[index - 2];
      const sigma0 = rotateRight(left, 7) ^ rotateRight(left, 18) ^ (left >>> 3);
      const sigma1 = rotateRight(right, 17) ^ rotateRight(right, 19) ^ (right >>> 10);
      schedule[index] = (schedule[index - 16] + sigma0 + schedule[index - 7] + sigma1) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = hash;
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temporary1 = (h + sum1 + choice + SHA256_CONSTANTS[index] + schedule[index]) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temporary2 = (sum0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temporary1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temporary1 + temporary2) >>> 0;
    }
    hash[0] = (hash[0] + a) >>> 0;
    hash[1] = (hash[1] + b) >>> 0;
    hash[2] = (hash[2] + c) >>> 0;
    hash[3] = (hash[3] + d) >>> 0;
    hash[4] = (hash[4] + e) >>> 0;
    hash[5] = (hash[5] + f) >>> 0;
    hash[6] = (hash[6] + g) >>> 0;
    hash[7] = (hash[7] + h) >>> 0;
  }
  return hash.map(valuePart => valuePart.toString(16).padStart(8, '0')).join('');
};

const stableValue = value => {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (Array.isArray(value)) return value.map(item => stableValue(item));
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      if (value[key] !== undefined && typeof value[key] !== 'function') {
        result[key] = stableValue(value[key]);
      }
      return result;
    }, {});
  }
  return null;
};

const stableJson = value => JSON.stringify(stableValue(value));
const sha256HexText = value => sha256HexBytes(utf8Bytes(value));

const normalizeProfileColor = (value, fallback, label) => {
  const color = String(value || fallback);
  if (!/^#[0-9a-f]{6}$/iu.test(color)) {
    throw new TypeError(`${label} must be a six-digit hexadecimal color.`);
  }
  return color.toUpperCase();
};

const normalizeProductionLayoutProfile = profile => {
  const source = profile || {};
  const fallback = NEUTRAL_PRODUCTION_TEMPLATE;
  const typography = ['narration', 'dialogue', 'instruction', 'title'].reduce((result, role) => {
    const requested = source.typography && source.typography[role] || {};
    const safeDefault = fallback.typography[role];
    const fontFamily = String(requested.fontFamily || safeDefault.fontFamily).trim();
    if (!fontFamily) throw new TypeError(`layoutProfile.typography.${role}.fontFamily is required.`);
    result[role] = {
      fontFamily,
      fontWeight: String(requested.fontWeight || safeDefault.fontWeight),
      lineHeight: Number(clamp(finite(requested.lineHeight, safeDefault.lineHeight), 0.8, 3).toFixed(4))
    };
    return result;
  }, {});
  const paletteSource = source.palette || {};
  const normalized = {
    schema: PRODUCTION_LAYOUT_PROFILE_SCHEMA,
    id: String(source.id || 'custom-production-profile').trim() || 'custom-production-profile',
    version: String(source.version || PRODUCTION_LAYOUT_VERSION),
    name: String(source.name || 'Custom production storybook'),
    description: String(source.description || 'Persisted production typography, palette, and layout profile.'),
    palette: {
      mintPaper: normalizeProfileColor(paletteSource.mintPaper, fallback.palette.mintPaper, 'layoutProfile.palette.mintPaper'),
      creamPaper: normalizeProfileColor(paletteSource.creamPaper, fallback.palette.creamPaper, 'layoutProfile.palette.creamPaper'),
      ink: normalizeProfileColor(paletteSource.ink, fallback.palette.ink, 'layoutProfile.palette.ink'),
      warmInk: normalizeProfileColor(paletteSource.warmInk, fallback.palette.warmInk, 'layoutProfile.palette.warmInk'),
      accentBlue: normalizeProfileColor(paletteSource.accentBlue, fallback.palette.accentBlue, 'layoutProfile.palette.accentBlue'),
      foxOrange: normalizeProfileColor(paletteSource.foxOrange, fallback.palette.foxOrange, 'layoutProfile.palette.foxOrange')
    },
    typography,
    copyBudget: stableValue(source.copyBudget || fallback.copyBudget),
    layout: stableValue(source.layout || fallback.layout)
  };
  return deepFreeze(normalized);
};

const resolveProductionLayoutProfile = (templateId, persistedProfile) => {
  if (persistedProfile) return normalizeProductionLayoutProfile(persistedProfile);
  if (isSeriesTemplateId(templateId)) return getSeriesTemplate(templateId);
  return NEUTRAL_PRODUCTION_TEMPLATE;
};

const normalizeUnitRegion = (region, fallbackId = 'region') => {
  const x = clamp(finite(region && region.x), 0, 1);
  const y = clamp(finite(region && region.y), 0, 1);
  return {
    id: safeId(region && (region.id || region.label) || fallbackId),
    label: String(region && region.label || fallbackId),
    role: String(region && region.role || 'text-safe'),
    x: Number(x.toFixed(6)),
    y: Number(y.toFixed(6)),
    width: Number(clamp(finite(region && region.width), 0, 1 - x).toFixed(6)),
    height: Number(clamp(finite(region && region.height), 0, 1 - y).toFixed(6)),
    confidence: Number(clamp(finite(region && region.confidence, 1), 0, 1).toFixed(6))
  };
};

const createPlacementCandidateRegions = requiredRegion => {
  const requestedWidth = clamp(finite(requiredRegion && requiredRegion.width, 0.72), 0.2, 0.9);
  const requestedHeight = clamp(finite(requiredRegion && requiredRegion.height, 0.28), 0.12, 0.72);
  const margin = 0.045;
  const centeredX = clamp((1 - requestedWidth) / 2, margin, 1 - requestedWidth - margin);
  const centeredY = clamp((1 - requestedHeight) / 2, margin, 1 - requestedHeight - margin);
  const rightX = Math.max(margin, 1 - requestedWidth - margin);
  const bottomY = Math.max(margin, 1 - requestedHeight - margin);
  const values = [
    ['top', centeredX, margin],
    ['bottom', centeredX, bottomY],
    ['left', margin, centeredY],
    ['right', rightX, centeredY],
    ['top-left', margin, margin],
    ['top-right', rightX, margin],
    ['bottom-left', margin, bottomY],
    ['bottom-right', rightX, bottomY]
  ];
  return values.map(([id, x, y]) => normalizeUnitRegion({
    id, label: id, role: 'text-candidate', x, y,
    width: requestedWidth, height: requestedHeight, confidence: 1
  }, id));
};

const colorChannels = value => {
  const match = String(value || '#141414').match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return [20, 20, 20];
  const hex = match[1].length === 3
    ? match[1].split('').map(character => character + character).join('')
    : match[1];
  return [0, 2, 4].map(index => Number.parseInt(hex.slice(index, index + 2), 16));
};

const linearChannel = value => {
  const normalized = value / 255;
  return normalized <= 0.04045
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

const luminanceFor = (red, green, blue) => (
  0.2126 * linearChannel(red) + 0.7152 * linearChannel(green) + 0.0722 * linearChannel(blue)
);

const contrastRatio = (left, right) => {
  const brighter = Math.max(left, right);
  const darker = Math.min(left, right);
  return (brighter + 0.05) / (darker + 0.05);
};

const pixelLuminance = (rgba, index) => {
  const alpha = rgba[index + 3] / 255;
  const red = rgba[index] * alpha + 255 * (1 - alpha);
  const green = rgba[index + 1] * alpha + 255 * (1 - alpha);
  const blue = rgba[index + 2] * alpha + 255 * (1 - alpha);
  return luminanceFor(red, green, blue);
};

const regionSubjectClearance = (region, subjects) => {
  if (!subjects.length) return { minimum: 1, overlap: 0 };
  let minimum = 1;
  let overlap = 0;
  for (const subject of subjects) {
    const overlapWidth = Math.max(0, Math.min(region.x + region.width, subject.x + subject.width) - Math.max(region.x, subject.x));
    const overlapHeight = Math.max(0, Math.min(region.y + region.height, subject.y + subject.height) - Math.max(region.y, subject.y));
    const overlapArea = overlapWidth * overlapHeight;
    overlap = Math.max(overlap, overlapArea / Math.max(0.000001, region.width * region.height));
    const gapX = Math.max(subject.x - (region.x + region.width), region.x - (subject.x + subject.width), 0);
    const gapY = Math.max(subject.y - (region.y + region.height), region.y - (subject.y + subject.height), 0);
    minimum = Math.min(minimum, Math.hypot(gapX, gapY) / Math.SQRT2);
  }
  return {
    minimum: Number(clamp(minimum, 0, 1).toFixed(6)),
    overlap: Number(clamp(overlap, 0, 1).toFixed(6))
  };
};

const samplePixelRegion = ({ rgba, width, height, region, textLuminance, edgeThreshold, sampleStride }) => {
  const startX = clamp(Math.floor(region.x * width), 0, width - 1);
  const startY = clamp(Math.floor(region.y * height), 0, height - 1);
  const endX = clamp(Math.ceil((region.x + region.width) * width), startX + 1, width);
  const endY = clamp(Math.ceil((region.y + region.height) * height), startY + 1, height);
  let count = 0;
  let mean = 0;
  let secondMoment = 0;
  let edges = 0;
  let comparisons = 0;
  let worstContrast = Number.POSITIVE_INFINITY;
  for (let y = startY; y < endY; y += sampleStride) {
    for (let x = startX; x < endX; x += sampleStride) {
      const index = (y * width + x) * 4;
      const luminance = pixelLuminance(rgba, index);
      count += 1;
      const delta = luminance - mean;
      mean += delta / count;
      secondMoment += delta * (luminance - mean);
      worstContrast = Math.min(worstContrast, contrastRatio(textLuminance, luminance));
      if (x + sampleStride < endX) {
        comparisons += 1;
        if (Math.abs(luminance - pixelLuminance(rgba, index + sampleStride * 4)) >= edgeThreshold) edges += 1;
      }
      if (y + sampleStride < endY) {
        comparisons += 1;
        if (Math.abs(luminance - pixelLuminance(rgba, index + sampleStride * width * 4)) >= edgeThreshold) edges += 1;
      }
    }
  }
  const variance = count > 1 ? secondMoment / count : 0;
  const edgeDensity = comparisons > 0 ? edges / comparisons : 0;
  const complexity = clamp(edgeDensity * 0.62 + Math.sqrt(variance) * 1.35, 0, 1);
  return {
    samples: count,
    averageLuminance: Number(mean.toFixed(6)),
    luminanceVariance: Number(variance.toFixed(6)),
    edgeDensity: Number(edgeDensity.toFixed(6)),
    complexity: Number(complexity.toFixed(6)),
    worstContrast: Number((Number.isFinite(worstContrast) ? worstContrast : 1).toFixed(6))
  };
};

/**
 * Samples actual RGBA pixels. No vision-model coordinate or prose is trusted
 * here: the same bytes and regions produce the same metrics in every runtime.
 */
const analyzeRgbaImage = input => {
  const width = round(input && input.width);
  const height = round(input && input.height);
  if (width < 1 || height < 1) throw new TypeError('RGBA analysis requires positive width and height.');
  const rgba = asBytes(input && (input.rgba || input.data));
  if (rgba.byteLength !== width * height * 4) {
    throw new TypeError(`RGBA buffer length ${rgba.byteLength} does not match ${width}x${height}.`);
  }
  const dimensionHeader = utf8Bytes(`${width}x${height}|rgba|`);
  const hashInput = new Uint8Array(dimensionHeader.length + rgba.length);
  hashInput.set(dimensionHeader);
  hashInput.set(rgba, dimensionHeader.length);
  const imageHash = String(input && input.imageHash || sha256HexBytes(hashInput)).toLowerCase();
  if (!/^[a-f0-9]{64}$/u.test(imageHash)) throw new TypeError('imageHash must be a SHA-256 hex digest.');

  const textColor = String(input && input.textColor || '#141414');
  const [red, green, blue] = colorChannels(textColor);
  const textLuminance = luminanceFor(red, green, blue);
  const maximumSamples = clamp(round(input && input.maximumSamples || 120000), 1024, 1000000);
  const sampleStride = Math.max(1, Math.ceil(Math.sqrt(width * height / maximumSamples)));
  const edgeThreshold = clamp(finite(input && input.edgeThreshold, 0.12), 0.01, 1);
  const requiredTextRegion = {
    width: clamp(finite(input && input.requiredTextRegion && input.requiredTextRegion.width, 0.72), 0.05, 1),
    height: clamp(finite(input && input.requiredTextRegion && input.requiredTextRegion.height, 0.28), 0.05, 1)
  };
  const candidateRegions = Array.isArray(input && input.candidateRegions) && input.candidateRegions.length
    ? input.candidateRegions.map((region, index) => normalizeUnitRegion(region, `candidate-${index + 1}`))
    : createPlacementCandidateRegions(requiredTextRegion);
  const subjects = [
    ...(Array.isArray(input && input.subjectRegions) ? input.subjectRegions : []),
    ...(Array.isArray(input && input.faceRegions) ? input.faceRegions : []),
    ...(Array.isArray(input && input.characterRegions) ? input.characterRegions : []),
    ...(Array.isArray(input && input.importantObjectRegions) ? input.importantObjectRegions : [])
  ].map((region, index) => normalizeUnitRegion(region, `subject-${index + 1}`));
  const complexityLimit = clamp(finite(input && input.complexityLimit, 0.34), 0, 1);
  const minimumContrast = Math.max(1, finite(input && input.minimumContrast, 4.5));
  const globalMetrics = samplePixelRegion({
    rgba, width, height,
    region: { x: 0, y: 0, width: 1, height: 1 },
    textLuminance, edgeThreshold, sampleStride
  });
  const candidates = candidateRegions.map(region => {
    const metrics = samplePixelRegion({
      rgba, width, height, region, textLuminance, edgeThreshold, sampleStride
    });
    const subjectClearance = regionSubjectClearance(region, subjects);
    const fits = region.width + 0.000001 >= requiredTextRegion.width
      && region.height + 0.000001 >= requiredTextRegion.height;
    const verified = fits
      && metrics.worstContrast >= minimumContrast
      && metrics.complexity <= complexityLimit
      && subjectClearance.overlap <= 0.000001;
    return {
      id: region.id,
      region,
      ...metrics,
      subjectClearance,
      fits,
      verified
    };
  });
  const textSafeRegions = candidates.filter(candidate => candidate.verified).map(candidate => ({
    ...candidate.region,
    role: 'text-safe',
    confidence: Number(clamp(
      Math.min(candidate.worstContrast / Math.max(minimumContrast, 0.000001), 1)
      * (1 - candidate.complexity), 0, 1
    ).toFixed(6))
  }));
  const analysisCore = {
    schema: 'tiny-fox/pixel-placement-analysis@1',
    analyzerVersion: PIXEL_ANALYZER_VERSION,
    imageHash,
    width,
    height,
    textColor,
    minimumContrast,
    complexityLimit,
    sampleStride,
    requiredTextRegion,
    global: globalMetrics,
    subjectRegions: subjects,
    candidates,
    textSafeRegions
  };
  return deepFreeze({
    ...analysisCore,
    analysisHash: sha256HexText(stableJson(analysisCore)),
    status: 'complete'
  });
};

const placementBookBalance = (candidateId, previousCandidateId) => {
  if (!previousCandidateId) return 0.72;
  if (candidateId === previousCandidateId) return 0.15;
  const opposite = {
    top: 'bottom', bottom: 'top', left: 'right', right: 'left',
    'top-left': 'bottom-right', 'bottom-right': 'top-left',
    'top-right': 'bottom-left', 'bottom-left': 'top-right'
  };
  return opposite[previousCandidateId] === candidateId ? 1 : 0.68;
};

const scorePlacementCandidates = (analysis, options = {}) => {
  if (!analysis || analysis.status !== 'complete' || !Array.isArray(analysis.candidates)) return [];
  const required = options.requiredTextRegion || analysis.requiredTextRegion || { width: 0, height: 0 };
  // Normalized regions are persisted to six decimals while canvas geometry is
  // rounded to whole pixels. A two-thousandth tolerance is below two pixels on
  // supported canvases and prevents that serialization boundary becoming a
  // false unsafe result.
  const fitTolerance = clamp(finite(options.fitTolerance, 0.002), 0, 0.01);
  const order = new Map(PLACEMENT_IDS.map((id, index) => [id, index]));
  return analysis.candidates.map(candidate => {
    const fits = candidate.region.width + fitTolerance >= finite(required.width)
      && candidate.region.height + fitTolerance >= finite(required.height);
    const contrastScore = clamp(candidate.worstContrast / Math.max(4.5, finite(options.minimumContrast, 4.5)), 0, 1);
    const clearanceScore = clamp(candidate.subjectClearance && candidate.subjectClearance.minimum, 0, 1);
    const cropPreservation = 1 - clamp(candidate.subjectClearance && candidate.subjectClearance.overlap, 0, 1);
    const simplicity = 1 - clamp(candidate.complexity, 0, 1);
    const bookBalance = placementBookBalance(candidate.id, options.previousCandidateId);
    const score = (fits ? 0.18 : 0)
      + contrastScore * 0.24
      + clearanceScore * 0.2
      + cropPreservation * 0.16
      + simplicity * 0.14
      + bookBalance * 0.08;
    const verified = fits
      && candidate.worstContrast >= Math.max(4.5, finite(options.minimumContrast, 4.5))
      && candidate.complexity <= finite(options.complexityLimit, analysis.complexityLimit || 0.34)
      && finite(candidate.subjectClearance && candidate.subjectClearance.overlap) <= 0.000001;
    return { ...candidate, fits, verified, bookBalance, score: Number(score.toFixed(6)) };
  }).sort((left, right) => (
    Number(right.verified) - Number(left.verified)
    || right.score - left.score
    || (order.get(left.id) ?? 999) - (order.get(right.id) ?? 999)
    || left.id.localeCompare(right.id)
  ));
};

const isPlacementAnalysisCurrent = (analysis, imageHash) => Boolean(
  analysis
  && analysis.status === 'complete'
  && analysis.analyzerVersion === PIXEL_ANALYZER_VERSION
  && /^[a-f0-9]{64}$/u.test(String(imageHash || ''))
  && String(analysis.imageHash).toLowerCase() === String(imageHash).toLowerCase()
);

/** Returns null on an image/analyzer mismatch without mutating the stored page. */
const invalidatePlacementAnalysisOnImageChange = (analysis, imageHash) => (
  isPlacementAnalysisCurrent(analysis, imageHash) ? analysis : null
);

const createAuthoritativeMeasurementAdapter = options => {
  const settings = options || {};
  const available = settings.available !== false
    && settings.bundledFonts === true
    && settings.authoritative !== false
    && settings.fontAssetVersion === BUNDLED_FONT_ASSET_VERSION
    && typeof settings.measureText === 'function';
  return Object.freeze({
    schema: AUTHORITATIVE_MEASUREMENT_SCHEMA,
    authoritative: settings.authoritative !== false,
    available,
    bundledFonts: settings.bundledFonts === true,
    engine: String(settings.engine || 'unknown'),
    fontAssetVersion: String(settings.fontAssetVersion || ''),
    fontIdentity: String(settings.fontIdentity || `${settings.fontAssetVersion || 'missing'}:${settings.engine || 'unknown'}`),
    measureText: settings.measureText,
    verifyFonts: typeof settings.verifyFonts === 'function' ? settings.verifyFonts : undefined
  });
};

const normalizeProductionBlocks = blocks => (Array.isArray(blocks) ? blocks : []).map((block, sourceIndex) => ({
  ...(block && typeof block === 'object' ? block : {}),
  type: String(block && block.type || 'narration').toLowerCase(),
  character: block && block.character != null ? String(block.character) : undefined,
  content: String(block && block.content != null ? block.content : ''),
  sourceTextBoxId: String(block && block.sourceTextBoxId || `source-${sourceIndex + 1}`),
  sourceIndex
}));

const visibleContent = value => /\S/u.test(String(value == null ? '' : value));

const segmentGraphemeOffsets = content => {
  const value = String(content || '');
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    return Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value), entry => (
      entry.index + entry.segment.length
    ));
  }
  const offsets = [];
  let offset = 0;
  for (const codePoint of Array.from(value)) {
    offset += codePoint.length;
    offsets.push(offset);
  }
  return offsets;
};

const regexEndOffsets = (content, pattern) => {
  const offsets = [];
  pattern.lastIndex = 0;
  let match;
  while ((match = pattern.exec(content)) !== null) {
    offsets.push(match.index + match[0].length);
    if (!match[0].length) pattern.lastIndex += 1;
  }
  return offsets;
};

const splitBoundaryOffsets = (content, boundary) => {
  if (boundary === 'paragraph') return regexEndOffsets(content, /\r?\n[\t ]*\r?\n(?:[\t ]*\r?\n)*/g);
  if (boundary === 'sentence') {
    return regexEndOffsets(content, /[.!?\u2026\u3002\uFF01\uFF1F]+["'\u201D\u2019\u00BB\uFF09)\]}]*(?:\s+|(?=\S))/gu);
  }
  if (boundary === 'word') return regexEndOffsets(content, /\s+/gu);
  return segmentGraphemeOffsets(content);
};

const validSplitOffsets = (content, offsets) => Array.from(new Set(offsets))
  .filter(offset => (
    offset > 0 && offset < content.length
    && visibleContent(content.slice(0, offset))
    && visibleContent(content.slice(offset))
  ))
  .sort((left, right) => right - left);

const readingAgeFontFloor = readingAge => {
  const firstAge = Number.parseInt(String(readingAge || '').match(/\d+/u)?.[0] || '6', 10);
  if (firstAge <= 5) return 24;
  if (firstAge <= 8) return 20;
  return 18;
};

const DENSITY_SETTINGS = deepFreeze({
  airy: { scale: 1.08, gapRatio: 0.022, firstHeightRatio: 0.29, continuationHeightRatio: 0.74, integratedWordLimit: 28 },
  balanced: { scale: 1, gapRatio: 0.018, firstHeightRatio: 0.35, continuationHeightRatio: 0.78, integratedWordLimit: 36 },
  compact: { scale: 0.92, gapRatio: 0.014, firstHeightRatio: 0.42, continuationHeightRatio: 0.82, integratedWordLimit: 44 }
});

const assertOneOf = (value, supported, label, fallback) => {
  const normalized = String(value || fallback);
  if (!supported.includes(normalized)) throw new TypeError(`Unsupported ${label}: ${normalized}`);
  return normalized;
};

const typographyDescriptor = (template, block) => {
  let roleTypography;
  if (block.type === 'dialogue') roleTypography = template.typography.dialogue;
  else if (/instruction|fact|activity/u.test(block.type)) roleTypography = template.typography.instruction;
  else if (/title/u.test(block.type)) roleTypography = template.typography.title;
  else roleTypography = template.typography.narration;

  // Existing editor copy is already art-directed by the user. A layout-only
  // action may move or paginate that copy, but it must not silently restyle it
  // with the series/default role face. Keep role typography as the default for
  // generated copy while measuring explicit per-block typography exactly.
  const requestedFamily = String(block && block.fontFamily || '').trim();
  const requestedWeight = String(block && block.fontWeight || '').trim();
  const requestedStyle = String(block && block.fontStyle || '').trim().toLowerCase();
  return {
    fontFamily: requestedFamily || roleTypography.fontFamily,
    fontWeight: requestedWeight || roleTypography.fontWeight,
    fontStyle: requestedStyle === 'italic' ? 'italic' : 'normal',
    lineHeight: Number(clamp(
      finite(block && block.lineHeight, roleTypography.lineHeight),
      0.8,
      3
    ).toFixed(4))
  };
};

const textDirectionFor = content => {
  for (const character of String(content || '')) {
    if (/\p{Script_Extensions=Arabic}|\p{Script_Extensions=Hebrew}/u.test(character)) return 'rtl';
    if (/\p{Letter}|\p{Number}/u.test(character)) return 'ltr';
  }
  return 'ltr';
};

const normalizeMeasurement = (measurement, request) => {
  if (!measurement || typeof measurement !== 'object') {
    throw new Error('Authoritative text measurer returned no measurement.');
  }
  const height = finite(measurement.height, NaN);
  const lineCount = round(measurement.lineCount == null ? measurement.lines : measurement.lineCount);
  const maxLineWidth = finite(measurement.maxLineWidth == null ? measurement.width : measurement.maxLineWidth, 0);
  const requiresVisibleGeometry = visibleContent(request.content);
  if (!(height >= 0) || lineCount < 0 || !Number.isFinite(maxLineWidth)
    || requiresVisibleGeometry && (height <= 0 || lineCount < 1)) {
    throw new Error('Authoritative text measurer returned invalid geometry.');
  }
  return {
    height,
    lineCount,
    maxLineWidth,
    lineBreaks: Array.isArray(measurement.lineBreaks)
      ? measurement.lineBreaks.map(offset => round(offset)).filter(offset => offset >= 0 && offset <= request.content.length)
      : undefined
  };
};

const createMeasurementContext = ({ adapter, template, canvasSize, readingAge, density }) => {
  const settings = DENSITY_SETTINGS[density];
  const minimumDimension = Math.min(canvasSize.width, canvasSize.height);
  const floor = readingAgeFontFloor(readingAge);
  const preferred = clamp(round(minimumDimension * 0.032 * settings.scale), floor, 31);
  const gap = clamp(round(canvasSize.height * settings.gapRatio), 8, 28);
  const cache = new Map();

  const measureBlock = async (block, availableWidth) => {
    const typography = typographyDescriptor(template, block);
    const request = {
      content: String(block.content || ''),
      fontFamily: typography.fontFamily,
      fontWeight: typography.fontWeight,
      fontStyle: typography.fontStyle,
      fontSize: preferred,
      lineHeight: typography.lineHeight,
      width: availableWidth,
      direction: textDirectionFor(block.content),
      fontAssetVersion: adapter.fontAssetVersion,
      fontIdentity: adapter.fontIdentity
    };
    const key = stableJson(request);
    if (!cache.has(key)) {
      cache.set(key, Promise.resolve(adapter.measureText(request)).then(result => normalizeMeasurement(result, request)));
    }
    const measured = await cache.get(key);
    return { ...measured, request };
  };

  const measureBlocks = async (blocks, availableWidth) => {
    const measurements = [];
    for (const block of blocks) {
      if (!visibleContent(block.content)) continue;
      measurements.push({ block, measurement: await measureBlock(block, availableWidth) });
    }
    const totalHeight = measurements.reduce((sum, item) => sum + item.measurement.height, 0)
      + Math.max(0, measurements.length - 1) * gap;
    const widthFits = measurements.every(item => item.measurement.maxLineWidth <= availableWidth + 0.5);
    return { measurements, totalHeight, widthFits, fontSize: preferred, gap };
  };

  return { adapter, fontSize: preferred, gap, measureBlock, measureBlocks };
};

const firstVisibleGraphemeBlock = blocks => {
  for (const block of blocks) {
    const content = String(block.content || '');
    const firstOffset = segmentGraphemeOffsets(content).find(offset => visibleContent(content.slice(0, offset)));
    if (firstOffset != null) return { ...block, content: content.slice(0, firstOffset) };
  }
  return null;
};

const paginateProductionBlocks = async ({ blocks, pageFits }) => {
  const visibleBlocks = blocks.filter(block => visibleContent(block.content));
  const minimumFirst = firstVisibleGraphemeBlock(visibleBlocks);
  const preserveEmptyFirstPage = Boolean(minimumFirst && !(await pageFits([minimumFirst], 0)));
  const pageOffset = preserveEmptyFirstPage ? 1 : 0;
  const pages = [];
  let currentPage = [];
  const sourceMap = blocks.map((block, sourceIndex) => ({
    sourceIndex,
    sourceTextBoxId: block.sourceTextBoxId,
    outputs: []
  }));

  const flush = () => {
    if (!currentPage.length) return;
    pages.push(currentPage);
    currentPage = [];
  };
  const append = (source, sourceIndex, content, startOffset, endOffset, boundary) => {
    const outputs = sourceMap[sourceIndex].outputs;
    const fragment = {
      ...source,
      content,
      sourceIndex,
      fragmentIndex: outputs.length,
      startOffset,
      endOffset,
      boundary
    };
    outputs.push({
      pageIndex: pages.length + pageOffset,
      blockIndex: currentPage.length,
      fragmentIndex: fragment.fragmentIndex,
      startOffset,
      endOffset,
      boundary
    });
    currentPage.push(fragment);
  };

  for (let sourceIndex = 0; sourceIndex < blocks.length; sourceIndex += 1) {
    const source = blocks[sourceIndex];
    const original = String(source.content || '');
    if (!visibleContent(original)) continue;
    let sourceOffset = 0;
    while (sourceOffset < original.length) {
      const remainder = original.slice(sourceOffset);
      const complete = { ...source, content: remainder };
      const flowPageIndex = pages.length + pageOffset;
      if (await pageFits([...currentPage, complete], flowPageIndex)) {
        append(source, sourceIndex, remainder, sourceOffset, original.length, 'source-end');
        sourceOffset = original.length;
        continue;
      }
      if (currentPage.length && await pageFits([complete], flowPageIndex + 1)) {
        flush();
        continue;
      }

      let split = null;
      for (const boundary of ['paragraph', 'sentence', 'word', 'grapheme']) {
        const offsets = validSplitOffsets(remainder, splitBoundaryOffsets(remainder, boundary))
          .sort((left, right) => left - right);
        let lower = 0;
        let upper = offsets.length - 1;
        let bestOffset;
        while (lower <= upper) {
          const middle = Math.floor((lower + upper) / 2);
          const endOffset = offsets[middle];
          const fragment = { ...source, content: remainder.slice(0, endOffset) };
          if (await pageFits([...currentPage, fragment], flowPageIndex)) {
            bestOffset = endOffset;
            lower = middle + 1;
          } else {
            upper = middle - 1;
          }
        }
        if (bestOffset != null) split = { endOffset: bestOffset, boundary };
        if (split) break;
      }
      if (split) {
        append(
          source,
          sourceIndex,
          remainder.slice(0, split.endOffset),
          sourceOffset,
          sourceOffset + split.endOffset,
          split.boundary
        );
        sourceOffset += split.endOffset;
        flush();
        continue;
      }
      if (currentPage.length) {
        flush();
        continue;
      }
      throw new Error('Narration cannot fit even one grapheme using the authoritative production font.');
    }
  }
  flush();
  let outputPages = pages;
  if (preserveEmptyFirstPage) outputPages = [[], ...pages];
  if (!outputPages.length) outputPages = [[]];
  return { pages: outputPages, sourceMap };
};

const normalizeReferenceBindings = bindings => (Array.isArray(bindings) ? bindings : []).map((binding, index) => {
  const normalized = {
    id: String(binding && (binding.id || binding.assetId) || ''),
    version: String(binding && binding.version || ''),
    sha256: String(binding && (binding.sha256 || binding.hash) || '').toLowerCase(),
    kind: String(binding && binding.kind || 'reference'),
    role: String(binding && binding.role || '')
  };
  if (!normalized.id || !normalized.version || !/^[a-f0-9]{64}$/u.test(normalized.sha256)) {
    throw new TypeError(`Reference binding ${index + 1} requires id, version, and SHA-256 hash.`);
  }
  return normalized;
}).sort((left, right) => (
  left.id.localeCompare(right.id) || left.version.localeCompare(right.version) || left.sha256.localeCompare(right.sha256)
));

const createPlanningSnapshot = input => {
  const source = input || {};
  const persistedLayoutProfile = source.layoutProfile
    ? normalizeProductionLayoutProfile(source.layoutProfile)
    : undefined;
  const density = assertOneOf(source.textDensity, TEXT_DENSITIES, 'text density', 'balanced');
  const compositionPreference = assertOneOf(
    source.compositionPreference,
    QUICK_CREATE_COMPOSITION_MODES,
    'composition preference',
    'auto-book-rhythm'
  );
  const treatmentPreference = assertOneOf(
    source.treatmentPreference,
    QUICK_CREATE_TREATMENTS,
    'treatment preference',
    'paper-fade'
  );
  const pages = (Array.isArray(source.pages) ? source.pages : []).map((page, index) => ({
    id: String(page && page.id || `page-${index + 1}`),
    textBoxes: normalizeProductionBlocks(page && page.textBoxes).map(block => ({
      type: block.type,
      character: block.character,
      content: block.content,
      sourceTextBoxId: block.sourceTextBoxId,
      ...(String(block.fontFamily || '').trim()
        ? { fontFamily: String(block.fontFamily).trim() }
        : {}),
      ...(String(block.fontWeight || '').trim()
        ? { fontWeight: String(block.fontWeight).trim() }
        : {}),
      ...(String(block.fontStyle || '').trim()
        ? { fontStyle: String(block.fontStyle).trim().toLowerCase() }
        : {}),
      ...(Number.isFinite(Number(block.lineHeight))
        ? { lineHeight: Number(clamp(Number(block.lineHeight), 0.8, 3).toFixed(4)) }
        : {})
    })),
    hasIllustration: page && page.hasIllustration !== false,
    imageUrl: page && page.imageUrl ? String(page.imageUrl) : '',
    imageHash: page && page.imageHash ? String(page.imageHash).toLowerCase() : '',
    pageFamily: page && page.pageFamily ? String(page.pageFamily) : undefined,
    compositionOverride: page && page.compositionOverride ? stableValue(page.compositionOverride) : undefined,
    placementAnalysisHash: page && page.placementAnalysis && page.placementAnalysis.analysisHash
      ? String(page.placementAnalysis.analysisHash) : undefined
  }));
  pages.forEach((page, index) => {
    if (page.imageHash && !/^[a-f0-9]{64}$/u.test(page.imageHash)) {
      throw new TypeError(`pages.${index}.imageHash must be a SHA-256 hex digest.`);
    }
  });
  const canvasSize = {
    width: round(source.canvasSize && source.canvasSize.width),
    height: round(source.canvasSize && source.canvasSize.height)
  };
  if (canvasSize.width < 100 || canvasSize.height < 100) {
    throw new TypeError('Production canvasSize must contain dimensions of at least 100 pixels.');
  }
  return deepFreeze({
    schema: 'tiny-fox/production-planning-input@1',
    version: PRODUCTION_LAYOUT_VERSION,
    templateId: String(source.templateId || persistedLayoutProfile && persistedLayoutProfile.id || NEUTRAL_PRODUCTION_TEMPLATE_ID),
    layoutProfile: persistedLayoutProfile,
    canvasSize,
    readingAge: String(source.readingAge || '6-8'),
    designStyle: String(source.designStyle || 'storybook'),
    textDensity: density,
    compositionPreference,
    treatmentPreference,
    referenceBindings: normalizeReferenceBindings(source.referenceBindings),
    pages
  });
};

const resolveCompositionFallback = input => {
  const source = input || {};
  const requestedProtectedTreatment = source.requestedTreatment === 'solid-paper'
    ? 'solid-paper'
    : 'paper-fade';
  if (source.fontMeasurementAvailable === false) {
    return { step: 7, status: 'review-required', strategy: null, treatment: null, reason: 'FONT_MEASUREMENT_UNAVAILABLE' };
  }
  if (!source.hasText) {
    return { step: 1, status: 'ready', strategy: 'image-only', treatment: 'integrated', reason: 'NO_TEXT' };
  }
  if (source.hasIllustration === false || source.requestedStrategy === 'illustrated-text-page') {
    return { step: 6, status: 'ready', strategy: 'illustrated-text-page', treatment: 'solid-paper', reason: 'TEXT_PAGE_REQUESTED' };
  }

  const density = assertOneOf(source.textDensity, TEXT_DENSITIES, 'text density', 'balanced');
  const maximumIntegratedWords = finite(source.maximumIntegratedWords, DENSITY_SETTINGS[density].integratedWordLimit);
  const scored = scorePlacementCandidates(source.imageAnalysis, {
    requiredTextRegion: source.requiredTextRegion,
    previousCandidateId: source.previousCandidateId,
    minimumContrast: 4.5
  });
  const verified = scored.filter(candidate => candidate.verified);
  const preferredCandidateId = String(source.preferredCandidateId || 'top');
  const preferred = verified.find(candidate => candidate.id === preferredCandidateId);
  const mayIntegrate = finite(source.totalWords) <= maximumIntegratedWords
    && source.textFitsProtected !== false
    && source.requestedStrategy !== 'protected-panel'
    && source.requestedStrategy !== 'split-vignette'
    && source.compositionPreference !== 'separate-reading-panels';
  if (mayIntegrate && preferred) {
    return {
      step: 2, status: 'ready', strategy: 'quiet-zone', treatment: 'integrated',
      candidate: preferred, reason: 'VERIFIED_PRIMARY_QUIET_ZONE'
    };
  }
  if (mayIntegrate && verified.length) {
    return {
      step: 3, status: 'ready', strategy: 'quiet-zone', treatment: 'integrated',
      candidate: verified[0], reason: 'VERIFIED_ALTERNATE_QUIET_ZONE'
    };
  }
  if (source.requestedStrategy === 'split-vignette' && source.textFitsSplit !== false) {
    return {
      step: 5, status: 'ready', strategy: 'split-vignette',
      treatment: source.requestedTreatment === 'solid-paper' ? 'solid-paper' : 'framed-vignette',
      reason: 'BOOK_RHYTHM_SPLIT'
    };
  }
  if (source.textFitsProtected !== false) {
    return {
      step: 4, status: 'ready', strategy: 'protected-panel', treatment: requestedProtectedTreatment,
      reason: source.imageAnalysis ? 'QUIET_ZONE_REJECTED' : 'IMAGE_ANALYSIS_UNAVAILABLE'
    };
  }
  if (source.textFitsSplit !== false) {
    return {
      step: 5, status: 'ready', strategy: 'split-vignette',
      treatment: source.requestedTreatment === 'solid-paper' ? 'solid-paper' : 'framed-vignette',
      reason: 'PROTECTED_PANEL_CANNOT_FIT'
    };
  }
  return {
    step: 6, status: 'ready', strategy: 'illustrated-text-page', treatment: 'solid-paper',
    reason: 'CONTINUATION_REQUIRED', requiresContinuation: true
  };
};

const pageWordCount = page => normalizeProductionBlocks(page && page.textBoxes)
  .reduce((sum, block) => sum + wordsIn(block.content), 0);

const planWholeBookRhythm = (pages, options = {}) => {
  const compositionMode = assertOneOf(
    options.compositionMode || options.compositionPreference,
    QUICK_CREATE_COMPOSITION_MODES,
    'composition mode',
    'auto-book-rhythm'
  );
  const density = assertOneOf(options.textDensity, TEXT_DENSITIES, 'text density', 'balanced');
  const requestedTreatment = assertOneOf(
    options.treatment || options.treatmentPreference,
    QUICK_CREATE_TREATMENTS,
    'treatment',
    'paper-fade'
  );
  const normalPattern = [
    ['split-vignette', 'framed-vignette'],
    ['protected-panel', 'paper-fade'],
    ['quiet-zone', 'integrated'],
    ['split-vignette', 'solid-paper'],
    ['protected-panel', 'paper-fade']
  ];
  let normalIndex = 0;
  return (Array.isArray(pages) ? pages : []).map((page, pageIndex) => {
    const boxes = normalizeProductionBlocks(page && page.textBoxes);
    const totalWords = pageWordCount(page);
    const hasText = boxes.some(block => visibleContent(block.content));
    const dialogue = boxes.some(block => block.type === 'dialogue');
    let strategy;
    let treatment;
    let reason;
    if (!hasText) {
      strategy = 'image-only';
      treatment = 'integrated';
      reason = 'NO_TEXT';
    } else if (totalWords > DENSITY_SETTINGS[density].integratedWordLimit * 1.8) {
      strategy = 'illustrated-text-page';
      treatment = 'solid-paper';
      reason = 'LONG_COPY';
    } else if (dialogue) {
      strategy = 'protected-panel';
      treatment = requestedTreatment;
      reason = 'PROTECTED_DIALOGUE';
    } else if (compositionMode === 'separate-reading-panels') {
      strategy = 'protected-panel';
      treatment = requestedTreatment;
      reason = 'SEPARATE_PANEL_PREFERENCE';
    } else if (compositionMode === 'integrated-quiet-zones') {
      strategy = 'quiet-zone';
      treatment = 'integrated';
      reason = 'QUIET_ZONE_PREFERENCE';
    } else if (compositionMode === 'illustrated-text-pages') {
      strategy = 'illustrated-text-page';
      treatment = 'solid-paper';
      reason = 'ILLUSTRATED_TEXT_PREFERENCE';
    } else {
      [strategy, treatment] = normalPattern[normalIndex % normalPattern.length];
      normalIndex += 1;
      reason = 'AUTO_BOOK_RHYTHM';
    }
    const override = page && page.compositionOverride;
    if (override && PAGE_COMPOSITION_STRATEGIES.includes(override.strategy)
      && !(hasText && override.strategy === 'image-only')) {
      strategy = override.strategy;
      treatment = PAGE_COMPOSITION_TREATMENTS.includes(override.treatment)
        ? override.treatment
        : strategy === 'quiet-zone' || strategy === 'image-only' ? 'integrated'
          : strategy === 'split-vignette' ? 'framed-vignette' : 'paper-fade';
      if (strategy === 'quiet-zone' || strategy === 'image-only') treatment = 'integrated';
      if (strategy === 'illustrated-text-page') treatment = 'solid-paper';
      if (strategy === 'protected-panel' && treatment === 'integrated') treatment = 'paper-fade';
      reason = 'SAFE_PAGE_OVERRIDE';
    }
    return {
      pageId: String(page && page.id || `page-${pageIndex + 1}`),
      pageIndex,
      hasText,
      totalWords,
      strategy,
      treatment,
      reason,
      preferredCandidateId: pageIndex % 4 === 0 ? 'top'
        : pageIndex % 4 === 1 ? 'bottom'
          : pageIndex % 4 === 2 ? 'top-left' : 'bottom-right'
    };
  });
};

const createQuickCreateStoryboard = input => {
  const source = input || {};
  const controls = {
    compositionMode: assertOneOf(
      source.controls && source.controls.compositionMode,
      QUICK_CREATE_COMPOSITION_MODES,
      'composition mode',
      'auto-book-rhythm'
    ),
    textDensity: assertOneOf(
      source.controls && source.controls.textDensity,
      TEXT_DENSITIES,
      'text density',
      'balanced'
    ),
    treatment: assertOneOf(
      source.controls && source.controls.treatment,
      QUICK_CREATE_TREATMENTS,
      'treatment',
      'paper-fade'
    )
  };
  const pages = (Array.isArray(source.pages) ? source.pages : []).map((page, index) => ({
    ...page,
    id: String(page && page.id || `page-${index + 1}`),
    textBoxes: normalizeProductionBlocks(page && page.textBoxes)
  }));
  const rhythm = planWholeBookRhythm(pages, controls);
  const storyboardPages = rhythm.map((entry, index) => {
    const page = pages[index];
    const quietZoneRequested = entry.strategy === 'quiet-zone';
    return {
      pageId: entry.pageId,
      pageIndex: entry.pageIndex,
      totalWords: entry.totalWords,
      hasText: entry.hasText,
      requestedStrategy: entry.strategy,
      requestedTreatment: entry.treatment,
      strategy: quietZoneRequested ? 'protected-panel' : entry.strategy,
      treatment: quietZoneRequested ? 'paper-fade' : entry.treatment,
      preferredCandidateId: entry.preferredCandidateId,
      status: quietZoneRequested ? 'requires-image-analysis' : 'safe-preview',
      safeguard: quietZoneRequested ? 'PROTECTED_UNTIL_QUIET_ZONE_VERIFIED' : undefined,
      imageHash: String(page && page.imageHash || ''),
      regenerateText: false,
      regenerateArt: false,
      providerCallsRequired: 0
    };
  });
  const core = {
    schema: 'tiny-fox/quick-create-storyboard@1',
    version: PRODUCTION_LAYOUT_VERSION,
    controls,
    pages: storyboardPages,
    providerCallsRequired: 0
  };
  return deepFreeze({ ...core, storyboardHash: sha256HexText(stableJson(core)) });
};

const applyQuickCreateStoryboardOverride = (storyboard, pageId, override, options = {}) => {
  if (!storyboard || storyboard.schema !== 'tiny-fox/quick-create-storyboard@1') {
    throw new TypeError('A production Quick Create storyboard is required.');
  }
  const requestedStrategy = assertOneOf(
    override && override.strategy,
    PAGE_COMPOSITION_STRATEGIES,
    'page strategy',
    'protected-panel'
  );
  const requestedTreatment = assertOneOf(
    override && override.treatment,
    PAGE_COMPOSITION_TREATMENTS,
    'page treatment',
    requestedStrategy === 'quiet-zone' ? 'integrated' : 'paper-fade'
  );
  let found = false;
  const pages = storyboard.pages.map(page => {
    if (page.pageId !== String(pageId)) return { ...page };
    found = true;
    if (page.hasText && requestedStrategy === 'image-only') {
      throw new TypeError('Image-only cannot hide existing story text.');
    }
    const currentAnalysis = invalidatePlacementAnalysisOnImageChange(
      options.imageAnalysis,
      options.imageHash || page.imageHash
    );
    const verifiedQuiet = requestedStrategy === 'quiet-zone'
      ? scorePlacementCandidates(currentAnalysis, {
        previousCandidateId: options.previousCandidateId,
        minimumContrast: 4.5
      }).find(candidate => candidate.verified)
      : null;
    const guardedQuiet = requestedStrategy === 'quiet-zone' && !verifiedQuiet;
    return {
      ...page,
      requestedStrategy,
      requestedTreatment,
      strategy: guardedQuiet ? 'protected-panel' : requestedStrategy,
      treatment: guardedQuiet ? 'paper-fade' : requestedTreatment,
      preferredCandidateId: verifiedQuiet ? verifiedQuiet.id : page.preferredCandidateId,
      status: guardedQuiet ? 'requires-image-analysis' : 'safe-preview',
      safeguard: guardedQuiet ? 'PROTECTED_UNTIL_QUIET_ZONE_VERIFIED' : undefined,
      regenerateText: false,
      regenerateArt: false,
      providerCallsRequired: 0
    };
  });
  if (!found) throw new TypeError(`Storyboard page not found: ${String(pageId)}`);
  const core = {
    ...storyboard,
    pages,
    providerCallsRequired: 0
  };
  delete core.storyboardHash;
  return deepFreeze({ ...core, storyboardHash: sha256HexText(stableJson(core)) });
};

const productionTextCapacity = ({ canvasSize, density, continuationIndex }) => {
  const settings = DENSITY_SETTINGS[density];
  const continuation = continuationIndex > 0;
  return {
    x: round(canvasSize.width * 0.09),
    y: round(canvasSize.height * (continuation ? 0.1 : 0.07)),
    width: round(canvasSize.width * 0.82),
    height: round(canvasSize.height * (
      continuation ? settings.continuationHeightRatio : settings.firstHeightRatio
    ))
  };
};

const paperContrast = (paperColor, inkColor) => {
  const [paperRed, paperGreen, paperBlue] = colorChannels(paperColor);
  const [inkRed, inkGreen, inkBlue] = colorChannels(inkColor);
  return Number(contrastRatio(
    luminanceFor(paperRed, paperGreen, paperBlue),
    luminanceFor(inkRed, inkGreen, inkBlue)
  ).toFixed(6));
};

const toNormalizedBounds = (bounds, canvasSize, label) => normalizeUnitRegion({
  id: safeId(label), label, role: 'text-safe',
  x: bounds.x / canvasSize.width,
  y: bounds.y / canvasSize.height,
  width: bounds.width / canvasSize.width,
  height: bounds.height / canvasSize.height,
  confidence: 1
}, label);

const stableContinuationPageId = (sourcePageId, continuationIndex) => (
  continuationIndex === 0
    ? String(sourcePageId || 'page')
    : `${String(sourcePageId || 'page')}--continuation-${String(continuationIndex).padStart(2, '0')}`
);

const buildProductionGeometry = ({
  canvasSize, decision, requiredTextHeight, preferredCandidateId, pageIndex
}) => {
  const width = canvasSize.width;
  const height = canvasSize.height;
  const paddingX = round(width * 0.09);
  const paddingY = clamp(round(height * 0.025), 12, 32);
  const fadeSize = clamp(round(height * 0.065), 28, 80);
  let textBounds;
  let imageBounds = { x: 0, y: 0, width, height };
  let panelBounds;
  let fadeEdge = 'none';

  if (decision.strategy === 'image-only') {
    textBounds = { x: 0, y: 0, width: 0, height: 0 };
  } else if (decision.strategy === 'quiet-zone' && decision.candidate) {
    const region = decision.candidate.region;
    textBounds = {
      x: round(region.x * width), y: round(region.y * height),
      width: round(region.width * width), height: round(region.height * height)
    };
  } else if (decision.strategy === 'illustrated-text-page') {
    textBounds = {
      x: paddingX, y: round(height * 0.1),
      width: width - paddingX * 2, height: round(height * 0.8)
    };
    panelBounds = { x: 0, y: 0, width, height };
    imageBounds = { x: 0, y: 0, width: 0, height: 0 };
  } else {
    const panelHeight = clamp(
      round(requiredTextHeight + paddingY * 2 + (decision.treatment === 'paper-fade' ? fadeSize : 0)),
      round(height * 0.34),
      round(height * 0.58)
    );
    const panelAtTop = preferredCandidateId === 'top'
      || preferredCandidateId === 'top-left'
      || preferredCandidateId === 'top-right'
      || (!preferredCandidateId && pageIndex % 2 === 0);
    if (panelAtTop) {
      panelBounds = { x: 0, y: 0, width, height: panelHeight };
      fadeEdge = decision.treatment === 'paper-fade' ? 'bottom' : 'none';
      textBounds = {
        x: paddingX, y: paddingY,
        width: width - paddingX * 2,
        height: panelHeight - paddingY * 2 - (decision.treatment === 'paper-fade' ? fadeSize : 0)
      };
      imageBounds = {
        x: decision.strategy === 'split-vignette' ? round(width * 0.07) : 0,
        y: panelHeight - (decision.treatment === 'paper-fade' ? fadeSize : 0),
        width: decision.strategy === 'split-vignette' ? round(width * 0.86) : width,
        height: height - panelHeight + (decision.treatment === 'paper-fade' ? fadeSize : 0)
      };
    } else {
      panelBounds = { x: 0, y: height - panelHeight, width, height: panelHeight };
      fadeEdge = decision.treatment === 'paper-fade' ? 'top' : 'none';
      textBounds = {
        x: paddingX,
        y: height - panelHeight + paddingY + (decision.treatment === 'paper-fade' ? fadeSize : 0),
        width: width - paddingX * 2,
        height: panelHeight - paddingY * 2 - (decision.treatment === 'paper-fade' ? fadeSize : 0)
      };
      imageBounds = {
        x: decision.strategy === 'split-vignette' ? round(width * 0.07) : 0,
        y: 0,
        width: decision.strategy === 'split-vignette' ? round(width * 0.86) : width,
        height: height - panelHeight + (decision.treatment === 'paper-fade' ? fadeSize : 0)
      };
    }
  }
  return { textBounds, imageBounds, panelBounds, fadeEdge, fadeSize };
};

const buildProductionComposition = async ({
  sourcePage, flowPageId, blocks, pageIndex, continuationIndex, continuationCount,
  canvasSize, template, measurementContext, rhythmEntry, density, imageAnalysis,
  previousCandidateId
}) => {
  const capacity = productionTextCapacity({ canvasSize, density, continuationIndex });
  const capacityMeasurement = await measurementContext.measureBlocks(blocks, capacity.width);
  const requiredTextRegion = {
    width: Number((capacity.width / canvasSize.width).toFixed(6)),
    height: Number(clamp(
      (capacityMeasurement.totalHeight + capacityMeasurement.gap * 2) / canvasSize.height,
      0.12,
      continuationIndex > 0 ? 0.82 : 0.48
    ).toFixed(6))
  };
  const hasText = blocks.some(block => visibleContent(block.content));
  const requestedStrategy = continuationIndex > 0 ? 'illustrated-text-page' : rhythmEntry.strategy;
  const decision = resolveCompositionFallback({
    fontMeasurementAvailable: true,
    hasText,
    hasIllustration: sourcePage.hasIllustration !== false,
    totalWords: blocks.reduce((sum, block) => sum + wordsIn(block.content), 0),
    textDensity: density,
    compositionPreference: rhythmEntry.compositionPreference,
    requestedStrategy,
    requestedTreatment: rhythmEntry.treatment,
    preferredCandidateId: rhythmEntry.preferredCandidateId,
    previousCandidateId,
    requiredTextRegion,
    imageAnalysis,
    textFitsProtected: capacityMeasurement.widthFits
      && capacityMeasurement.totalHeight <= canvasSize.height * DENSITY_SETTINGS[density].firstHeightRatio,
    textFitsSplit: capacityMeasurement.widthFits && capacityMeasurement.totalHeight <= canvasSize.height * 0.46
  });
  const geometry = buildProductionGeometry({
    canvasSize,
    decision,
    requiredTextHeight: capacityMeasurement.totalHeight,
    preferredCandidateId: decision.candidate ? decision.candidate.id : rhythmEntry.preferredCandidateId,
    pageIndex
  });
  const finalMeasurement = await measurementContext.measureBlocks(blocks, geometry.textBounds.width || capacity.width);
  if (hasText && (
    !finalMeasurement.widthFits
    || finalMeasurement.totalHeight > geometry.textBounds.height + 0.5
  )) {
    throw new Error(`Authoritative text geometry overflows production page ${flowPageId}.`);
  }
  let nextY = geometry.textBounds.y;
  const textElements = finalMeasurement.measurements.map(({ block, measurement }, index) => {
    const typography = typographyDescriptor(template, block);
    const element = {
      id: `${flowPageId}-text-${block.sourceIndex + 1}-${block.fragmentIndex + 1}`,
      content: block.content,
      x: geometry.textBounds.x,
      y: round(nextY),
      width: geometry.textBounds.width,
      height: Math.ceil(measurement.height),
      fontSize: measurement.request.fontSize,
      fontFamily: typography.fontFamily,
      fontWeight: typography.fontWeight,
      fontStyle: typography.fontStyle,
      lineHeight: typography.lineHeight,
      padding: 0,
      color: template.palette.ink,
      rotation: 0,
      backgroundColor: 'transparent',
      backgroundOpacity: 0,
      borderWidth: 0,
      textAlign: measurement.request.direction === 'rtl' ? 'right' : 'left',
      verticalAlign: 'top',
      layoutRole: textRoleFor(block),
      sourceTextBoxId: block.sourceTextBoxId,
      sourceIndex: block.sourceIndex,
      sourceStartOffset: block.startOffset,
      sourceEndOffset: block.endOffset,
      measuredLineCount: measurement.lineCount,
      measuredLineBreaks: measurement.lineBreaks,
      measurementIdentity: measurement.request.fontIdentity,
      fontAssetVersion: measurement.request.fontAssetVersion
    };
    nextY += measurement.height + finalMeasurement.gap;
    return element;
  });
  const paperColor = pageIndex % 2 === 0 ? template.palette.creamPaper : template.palette.mintPaper;
  const panelElements = geometry.panelBounds ? [{
    id: `${flowPageId}-panel-1`,
    x: geometry.panelBounds.x,
    y: geometry.panelBounds.y,
    width: geometry.panelBounds.width,
    height: geometry.panelBounds.height,
    treatment: decision.treatment,
    backgroundColor: paperColor,
    opacity: 1,
    fadeEdge: geometry.fadeEdge,
    fadeSize: geometry.fadeSize,
    cornerRadius: decision.treatment === 'framed-vignette' ? clamp(round(canvasSize.width * 0.025), 8, 24) : 0,
    borderWidth: decision.treatment === 'framed-vignette' ? 2 : 0,
    borderColor: template.palette.accentBlue,
    layoutRole: 'reading-panel'
  }] : [];
  const imageUrl = String(sourcePage.imageUrl || '');
  const imageVisible = sourcePage.hasIllustration !== false
    && geometry.imageBounds.width > 0 && geometry.imageBounds.height > 0;
  const imageElements = imageVisible && imageUrl ? [{
    id: `${flowPageId}-illustration-1`,
    url: imageUrl,
    x: geometry.imageBounds.x,
    y: geometry.imageBounds.y,
    width: geometry.imageBounds.width,
    height: geometry.imageBounds.height,
    rotation: 0,
    fit: decision.strategy === 'image-only' || decision.strategy === 'quiet-zone' ? 'cover' : 'contain',
    feather: decision.strategy === 'split-vignette' ? 8 : 0,
    bleed: decision.strategy === 'image-only' || decision.strategy === 'quiet-zone' ? 'full' : 'none',
    layoutRole: 'illustration',
    altText: String(sourcePage.altText || 'Story illustration')
  }] : [];
  const safeRegion = toNormalizedBounds(geometry.textBounds, canvasSize, `${flowPageId}-text-safe`);
  const selectedCandidate = decision.candidate;
  const contrast = selectedCandidate
    ? selectedCandidate.worstContrast
    : paperContrast(paperColor, template.palette.ink);
  const complexity = selectedCandidate ? selectedCandidate.complexity : 0;
  const subjectClearance = selectedCandidate
    ? selectedCandidate.subjectClearance
    : { minimum: 1, overlap: 0 };
  return {
    schema: PAGE_COMPOSITION_SCHEMA_V2,
    version: PRODUCTION_LAYOUT_VERSION,
    templateId: template.id,
    pageId: flowPageId,
    sourcePageId: String(sourcePage.id),
    continuationIndex,
    continuationCount,
    strategy: decision.strategy,
    treatment: decision.treatment,
    fallbackStep: decision.step,
    fallbackReason: decision.reason,
    backgroundColor: paperColor,
    canvasSize: { ...canvasSize },
    textBounds: geometry.textBounds,
    imageBounds: geometry.imageBounds,
    textElements,
    panelElements,
    imageElements,
    imageRequired: imageVisible && !imageUrl,
    providerAspectRatio: providerAspectRatioFor(geometry.imageBounds),
    totalWords: blocks.reduce((sum, block) => sum + wordsIn(block.content), 0),
    textSafeRegions: hasText ? [safeRegion] : [],
    imageHash: String(sourcePage.imageHash || ''),
    analyzerVersion: imageAnalysis && imageAnalysis.analyzerVersion,
    analysisHash: imageAnalysis && imageAnalysis.analysisHash,
    placementAnalysis: imageAnalysis || undefined,
    worstContrast: contrast,
    complexity,
    subjectClearance,
    measurement: {
      schema: AUTHORITATIVE_MEASUREMENT_SCHEMA,
      fontAssetVersion: measurementContext.adapter && measurementContext.adapter.fontAssetVersion,
      fontIdentity: measurementContext.adapter && measurementContext.adapter.fontIdentity
    },
    requiresContinuation: continuationCount > 1,
    warnings: []
  };
};

const productionReviewResult = ({ snapshot, snapshotHash, adapter, reason, message, rhythm }) => ({
  schema: PRODUCTION_PAGE_FLOW_SCHEMA,
  version: PRODUCTION_LAYOUT_VERSION,
  status: 'review-required',
  providerCallsAllowed: false,
  reviewReasons: [{ code: reason, message }],
  snapshot,
  snapshotHash,
  fontMeasurement: {
    schema: AUTHORITATIVE_MEASUREMENT_SCHEMA,
    authoritative: Boolean(adapter && adapter.authoritative),
    available: Boolean(adapter && adapter.available),
    bundledFonts: Boolean(adapter && adapter.bundledFonts),
    engine: String(adapter && adapter.engine || 'unavailable'),
    fontAssetVersion: String(adapter && adapter.fontAssetVersion || ''),
    fontIdentity: String(adapter && adapter.fontIdentity || '')
  },
  rhythm: rhythm || [],
  pages: [],
  sourceMaps: []
});

/**
 * Copy-first production planner. The returned page/task IDs are derived only
 * from source page IDs and continuation indices, so retries and layout-only
 * regeneration cannot create a different task graph for identical copy.
 */
const planProductionPageFlow = async (input, suppliedAdapter) => {
  const source = input || {};
  const snapshot = createPlanningSnapshot(source);
  const snapshotHash = sha256HexText(stableJson(snapshot));
  const template = resolveProductionLayoutProfile(snapshot.templateId, snapshot.layoutProfile);
  const adapter = suppliedAdapter && suppliedAdapter.schema === AUTHORITATIVE_MEASUREMENT_SCHEMA
    ? suppliedAdapter
    : createAuthoritativeMeasurementAdapter(suppliedAdapter);
  const rhythm = planWholeBookRhythm(snapshot.pages, {
    compositionPreference: snapshot.compositionPreference,
    textDensity: snapshot.textDensity,
    treatmentPreference: snapshot.treatmentPreference
  }).map(entry => ({ ...entry, compositionPreference: snapshot.compositionPreference }));
  if (!adapter || !adapter.available || !adapter.authoritative || !adapter.bundledFonts
    || adapter.fontAssetVersion !== BUNDLED_FONT_ASSET_VERSION
    || typeof adapter.measureText !== 'function') {
    return productionReviewResult({
      snapshot, snapshotHash, adapter, rhythm,
      reason: 'AUTHORITATIVE_FONT_MEASUREMENT_UNAVAILABLE',
      message: 'Pinned production fonts are unavailable; no generation tasks may be finalized.'
    });
  }

  const allBlocks = snapshot.pages.flatMap(page => normalizeProductionBlocks(page.textBoxes));
  if (adapter.verifyFonts) {
    const fontRequests = Array.from(new Map(allBlocks.filter(block => visibleContent(block.content)).map(block => {
      const typography = typographyDescriptor(template, block);
      return [`${typography.fontFamily}|${typography.fontWeight}`, {
        family: typography.fontFamily,
        weight: typography.fontWeight,
        sample: block.content,
        fontAssetVersion: adapter.fontAssetVersion,
        fontIdentity: adapter.fontIdentity
      }];
    })).values());
    try {
      const verification = await adapter.verifyFonts(fontRequests);
      if (verification === false || verification && verification.available === false) {
        throw new Error('The font verifier reported an unavailable production font.');
      }
    } catch (error) {
      return productionReviewResult({
        snapshot, snapshotHash, adapter, rhythm,
        reason: 'AUTHORITATIVE_FONT_MEASUREMENT_UNAVAILABLE',
        message: error instanceof Error ? error.message : String(error)
      });
    }
  }

  const measurementContext = createMeasurementContext({
    adapter,
    template,
    canvasSize: snapshot.canvasSize,
    readingAge: snapshot.readingAge,
    density: snapshot.textDensity
  });
  const plannedPages = [];
  const sourceMaps = [];
  let previousCandidateId;
  try {
    for (let sourceIndex = 0; sourceIndex < snapshot.pages.length; sourceIndex += 1) {
      const snapshotPage = snapshot.pages[sourceIndex];
      const runtimePage = Array.isArray(source.pages) && source.pages[sourceIndex]
        ? source.pages[sourceIndex]
        : snapshotPage;
      const blocks = normalizeProductionBlocks(snapshotPage.textBoxes);
      const pagination = await paginateProductionBlocks({
        blocks,
        pageFits: async (candidateBlocks, continuationIndex) => {
          const capacityContinuationIndex = rhythm[sourceIndex].strategy === 'illustrated-text-page'
            ? Math.max(1, continuationIndex)
            : continuationIndex;
          const capacity = productionTextCapacity({
            canvasSize: snapshot.canvasSize,
            density: snapshot.textDensity,
            continuationIndex: capacityContinuationIndex
          });
          const measured = await measurementContext.measureBlocks(candidateBlocks, capacity.width);
          return measured.widthFits && measured.totalHeight <= capacity.height + 0.5;
        }
      });
      const continuationCount = pagination.pages.length;
      const runtimeImageHash = String(runtimePage.imageHash || snapshotPage.imageHash || '').toLowerCase();
      const currentAnalysis = invalidatePlacementAnalysisOnImageChange(
        runtimePage.placementAnalysis,
        runtimeImageHash || runtimePage.placementAnalysis && runtimePage.placementAnalysis.imageHash
      );
      for (let continuationIndex = 0; continuationIndex < continuationCount; continuationIndex += 1) {
        const flowPageId = stableContinuationPageId(snapshotPage.id, continuationIndex);
        const composition = await buildProductionComposition({
          sourcePage: {
            ...snapshotPage,
            ...runtimePage,
            id: snapshotPage.id,
            textBoxes: snapshotPage.textBoxes,
            imageHash: runtimeImageHash
          },
          flowPageId,
          blocks: pagination.pages[continuationIndex],
          pageIndex: plannedPages.length,
          continuationIndex,
          continuationCount,
          canvasSize: snapshot.canvasSize,
          template,
          measurementContext,
          rhythmEntry: rhythm[sourceIndex],
          density: snapshot.textDensity,
          imageAnalysis: continuationIndex === 0 ? currentAnalysis : null,
          previousCandidateId
        });
        if (composition.strategy === 'quiet-zone') {
          previousCandidateId = composition.fallbackReason === 'VERIFIED_PRIMARY_QUIET_ZONE'
            ? rhythm[sourceIndex].preferredCandidateId
            : composition.placementAnalysis && scorePlacementCandidates(composition.placementAnalysis, {
              previousCandidateId
            })[0]?.id;
        }
        plannedPages.push({
          id: flowPageId,
          sourcePageId: snapshotPage.id,
          continuationIndex,
          continuationCount,
          blocks: pagination.pages[continuationIndex],
          composition
        });
      }
      sourceMaps.push({ sourcePageId: snapshotPage.id, entries: pagination.sourceMap });
    }
  } catch (error) {
    return productionReviewResult({
      snapshot, snapshotHash, adapter, rhythm,
      reason: /font|measure|geometry|grapheme|overflow/i.test(String(error && error.message || error))
        ? 'AUTHORITATIVE_TEXT_LAYOUT_FAILED'
        : 'PRODUCTION_PAGE_FLOW_FAILED',
      message: error instanceof Error ? error.message : String(error)
    });
  }

  return {
    schema: PRODUCTION_PAGE_FLOW_SCHEMA,
    version: PRODUCTION_LAYOUT_VERSION,
    status: 'ready',
    providerCallsAllowed: true,
    reviewReasons: [],
    snapshot,
    snapshotHash,
    fontMeasurement: {
      schema: AUTHORITATIVE_MEASUREMENT_SCHEMA,
      authoritative: true,
      available: true,
      bundledFonts: true,
      engine: adapter.engine,
      fontAssetVersion: adapter.fontAssetVersion,
      fontIdentity: adapter.fontIdentity
    },
    rhythm,
    pages: plannedPages,
    sourceMaps
  };
};

const reconstructProductionSourceText = (flow, sourcePageId, sourceIndex) => {
  if (!flow || !Array.isArray(flow.pages)) return '';
  return flow.pages
    .filter(page => page.sourcePageId === String(sourcePageId))
    .sort((left, right) => left.continuationIndex - right.continuationIndex)
    .flatMap(page => page.blocks || [])
    .filter(block => finite(block.sourceIndex, -1) === Number(sourceIndex))
    .sort((left, right) => finite(left.startOffset) - finite(right.startOffset))
    .map(block => String(block.content || ''))
    .join('');
};

module.exports = {
  SERIES_TEMPLATE_SCHEMA,
  PAGE_COMPOSITION_SCHEMA,
  PAGE_COMPOSITION_SCHEMA_V2,
  PRODUCTION_PAGE_FLOW_SCHEMA,
  AUTHORITATIVE_MEASUREMENT_SCHEMA,
  PRODUCTION_LAYOUT_PROFILE_SCHEMA,
  STORYBOOK_LAYOUT_VERSION,
  PRODUCTION_LAYOUT_VERSION,
  BUNDLED_FONT_ASSET_VERSION,
  PIXEL_ANALYZER_VERSION,
  PAGE_COMPOSITION_STRATEGIES,
  PAGE_COMPOSITION_TREATMENTS,
  QUICK_CREATE_COMPOSITION_MODES,
  TEXT_DENSITIES,
  QUICK_CREATE_TREATMENTS,
  QUICK_CREATE_CONTROL_OPTIONS,
  TINY_LITTLE_FOX_SEQUEL_TEMPLATE_ID,
  TINY_LITTLE_FOX_SEQUEL_TEMPLATE,
  NEUTRAL_PRODUCTION_TEMPLATE_ID,
  NEUTRAL_PRODUCTION_TEMPLATE,
  SERIES_TEMPLATES,
  getSeriesTemplate,
  isSeriesTemplateId,
  createSeriesStyleDirection,
  resolvePageFamily,
  planSeriesPage,
  createSeriesIllustrationPrompt,
  sha256HexBytes,
  analyzeRgbaImage,
  createPlacementCandidateRegions,
  scorePlacementCandidates,
  isPlacementAnalysisCurrent,
  invalidatePlacementAnalysisOnImageChange,
  createAuthoritativeMeasurementAdapter,
  normalizeProductionLayoutProfile,
  resolveProductionLayoutProfile,
  createPlanningSnapshot,
  resolveCompositionFallback,
  stableContinuationPageId,
  planWholeBookRhythm,
  createQuickCreateStoryboard,
  applyQuickCreateStoryboardOverride,
  planProductionPageFlow,
  reconstructProductionSourceText
};
